import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { preReleaseService, PreReleaseProject, ProjectInterest, ProjectFeedback } from '../../services/preReleaseService';
import { PricingService } from '../../services/pricingService';
import { InterestModal } from '../../components/prerelease/InterestModal';
import { FeedbackSection } from '../../components/prerelease/FeedbackSection';
import PreReleaseSetupStatus from '../../components/prerelease/PreReleaseSetupStatus';
import ReleaseNotificationModal from '../../components/notifications/ReleaseNotificationModal';
import { EmailTemplateData, WebsiteMessageData } from '../../services/postReleaseNotificationService';
import { showToast } from '../../utils/toast';

import { Avatar } from '../../components/common/Avatar';
import '../../styles/pages/PreReleaseProjectDetail.css';
import '../../styles/components/prerelease/PreReleaseModals.css';
import '../../styles/components/prerelease/PreReleaseSetupStatus.css';

export const PreReleaseProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [project, setProject] = useState<PreReleaseProject | null>(null);
  const [userInterest, setUserInterest] = useState<ProjectInterest | null>(null);
  const [interestSummary, setInterestSummary] = useState<any>(null);
  const [feedback, setFeedback] = useState<ProjectFeedback[]>([]);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [showReleaseNotificationModal, setShowReleaseNotificationModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupRequired, setSetupRequired] = useState(false);
  const [releaseSuccess, setReleaseSuccess] = useState<string | null>(null);


  useEffect(() => {
    if (id) {
      loadProjectData();
    }
  }, [id]);

  const loadProjectData = async () => {
    try {
      setLoading(true);
      setError(null);
      setSetupRequired(false);

      // Check if setup is required by monitoring console warnings
      const originalConsoleWarn = console.warn;
      let setupWarningDetected = false;
      console.warn = (...args) => {
        if (args[0]?.includes('Pre-release functions not found') || args[0]?.includes('Pre-release tables not found')) {
          setupWarningDetected = true;
        }
        originalConsoleWarn(...args);
      };

      // Parallelize all API calls for better performance
      const [projectData, interestSummaryData, feedbackData, userInterestData] = await Promise.all([
        preReleaseService.getProjectById(id!),
        preReleaseService.getProjectInterestSummary(id!),
        preReleaseService.getProjectFeedback(id!),
        user ? preReleaseService.getUserInterest(id!) : Promise.resolve(null)
      ]);

      // Restore original console.warn
      console.warn = originalConsoleWarn;

      if (setupWarningDetected) {
        setSetupRequired(true);
        return;
      }

      if (!projectData) {
        setError('Project not found');
        return;
      }

      setProject(projectData);
      setInterestSummary(interestSummaryData);
      setFeedback(feedbackData);
      setUserInterest(userInterestData);
    } catch (err) {
      console.error('Error loading project data:', err);
      setSetupRequired(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInterestSubmitted = async (interest: ProjectInterest) => {
    try {
      // Update the user interest state immediately for responsive UI
      setUserInterest(interest);
      setShowInterestModal(false);

      // Optionally reload project data to get updated interest counts
      // This is non-blocking to maintain responsive UI
      loadProjectData();
    } catch (error) {
      console.error('Error updating UI after interest submission:', error);
      // Even if there's an error, we still want to close the modal
      setShowInterestModal(false);
    }
  };

  const handleFeedbackAdded = () => {
    loadProjectData();
  };

  // Use centralized pricing service
  const getPricingDisplay = () => {
    if (!project) return null;
    return PricingService.generatePricingDisplay(project);
  };

  const getDetailedPricingText = () => {
    if (!project) return null;
    return PricingService.getDetailedPricingText(project, t);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'app': return '📱';
      case 'website': return '🌐';
      case 'product': return '📦';
      case 'service': return '⚙️';
      default: return '💡';
    }
  };

  const handleImageClick = (src: string, alt: string) => {
    window.open(src, '_blank');
  };

  const handleReleaseProject = async () => {
    if (!project || !user || project.publisher_id !== user.id) return;

    try {
      // Check if project can be released
      const { canRelease, reason } = await preReleaseService.canReleaseProject(project.id);

      if (!canRelease) {
        setError(reason || 'Cannot release project at this time.');
        return;
      }

      // Release the project using the service method
      await preReleaseService.releaseProject(project.id);

      // Update local state
      setProject(prev => prev ? { ...prev, status: 'released', release_date: new Date().toISOString() } : null);

      // Show notification modal for interested users
      setShowReleaseNotificationModal(true);

    } catch (error) {
      console.error('Error releasing project:', error);
      setError('Failed to release project. Please try again.');
    }
  };

  const handleEmailTemplateGenerate = (templateData: EmailTemplateData) => {
    // Create downloadable file with email template
    const templateContent = `
Subject: ${templateData.subject}

Recipients: ${templateData.recipients.join(', ')}

HTML Content:
${templateData.htmlContent}

Plain Text Content:
${templateData.plainTextContent}
    `.trim();

    const blob = new Blob([templateContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project?.title || 'project'}-release-email-template.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setReleaseSuccess(`Email template generated successfully! ${templateData.recipients.length} recipients included.`);
  };

  const handleWebsiteMessageSend = (messageData: WebsiteMessageData) => {
    setReleaseSuccess(`Website messages sent successfully to ${messageData.recipients.length} interested users!`);
  };

  if (loading) {
    return (
      <div className="project-detail-page">
        <div className="loading-ring">Loading project...</div>
      </div>
    );
  }

  // Show setup status if database setup is required
  if (setupRequired) {
    return <PreReleaseSetupStatus onRetry={loadProjectData} />;
  }

  if (error || !project) {
    return (
      <div className="project-detail-page">
        <div className="error-message">
          <h2>{error || 'Project not found'}</h2>
          <Link to="/pre-release" className="back-link">← Back to Projects</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail-page">
      <div className="project-header">
        <div className="project-header-row-1">
          <div className="project-type">
            <span className="type-icon">{getTypeIcon(project.project_type)}</span>
            <span className="type-text">{project.project_type}</span>
          </div>
          <h1 className="project-title">{project.title}</h1>
          <div className="publisher-info">
            <Avatar
              src={project.publisher?.avatar_url}
              username={project.publisher?.username}
              className="publisher-avatar"
              size={28}
            />
            <span className="publisher-name">by {project.publisher?.username}</span>
          </div>

          {project.project_category && (
            <div className="project-category">{project.project_category}</div>
          )}
        </div>


      </div>

      {/* Success Message */}
      {releaseSuccess && (
        <div className="success-message">
          <div className="success-content">
            <i className="bi bi-check-circle"></i>
            <span>{releaseSuccess}</span>
            <button
              className="dismiss-success"
              onClick={() => setReleaseSuccess(null)}
              title="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="project-content">
        <div className="main-content">
          {project.image_url && (
            <div
              className="project-image clickable"
              onClick={() => handleImageClick(project.image_url!, project.title)}
            >
              <img src={project.image_url} alt={project.title} />
            </div>
          )}

          <div className="project-description">
            <h2>About This Project</h2>
            <p>{project.description}</p>
          </div>

          {project.concept_details && (
            <div className="concept-details">
              <h2>Detailed Concept</h2>
              <div className="concept-text">
                {project.concept_details.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
          )}

          {project.key_features && project.key_features.length > 0 && (() => {
            return (
              <div className="key-features">
                <h2>Key Features</h2>
                <div className="key-features-enhanced">
                  {project.key_features.map((feature, index) => {
                    // Handle different data formats
                    let featureText: string;
                    let screenshotUrl: string | undefined;

                    if (typeof feature === 'string') {
                      // Check if it's a JSON string that needs parsing
                      try {
                        const parsed = JSON.parse(feature);
                        if (parsed && typeof parsed === 'object' && parsed.text) {
                          featureText = parsed.text;
                          screenshotUrl = parsed.screenshot_url;
                        } else {
                          // If parsed but doesn't have expected structure, use original string
                          featureText = feature;
                          screenshotUrl = project.feature_screenshots?.[index]?.url;
                        }
                      } catch (e) {
                        // Not a JSON string, treat as regular string
                        featureText = feature;
                        screenshotUrl = project.feature_screenshots?.[index]?.url;
                      }
                    } else if (typeof feature === 'object' && feature !== null) {
                      // Already an object
                      featureText = feature.text || String(feature);
                      screenshotUrl = feature.screenshot_url;
                    } else {
                      // Fallback
                      featureText = String(feature);
                      screenshotUrl = undefined;
                    }

                    // Clean up the feature text - remove any remaining JSON artifacts
                    if (featureText.includes('{"text":') || featureText.includes('"screenshot_url":')) {
                      // This looks like malformed JSON, try to extract just the text part
                      const textMatch = featureText.match(/"text":"([^"]+)"/);
                      if (textMatch) {
                        featureText = textMatch[1];
                      } else {
                        // If we can't extract text, use a generic feature name
                        featureText = `Feature ${index + 1}`;
                      }
                    }

                    return (
                      <div key={index} className="feature-item">
                        <div className="feature-content">
                          <h4 className="feature-title">{featureText}</h4>
                        </div>
                        {screenshotUrl && (
                          <div
                            className="feature-screenshot clickable"
                            onClick={() => handleImageClick(screenshotUrl!, featureText)}
                          >
                            <img
                              src={screenshotUrl}
                              alt={featureText}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {project.feature_screenshots && project.feature_screenshots.length > project.key_features.length && (
                  <div className="feature-screenshots-gallery">
                    <h4>Additional Screenshots</h4>
                    <div className="screenshots-grid">
                      {project.feature_screenshots.slice(project.key_features.length).map((screenshot, index) => (
                        <div
                          key={index}
                          className="screenshot-item clickable"
                          onClick={() => handleImageClick(screenshot.url, screenshot.caption || `Screenshot ${index + 1}`)}
                        >
                          <img src={screenshot.url} alt={screenshot.caption || `Screenshot ${index + 1}`} />
                          {screenshot.caption && (
                            <div className="screenshot-caption">{screenshot.caption}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {project.demo_url && (
            <div className="demo-section">
              <h2>Demo / Preview</h2>
              <a
                href={project.demo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="demo-link"
              >
                View Demo →
              </a>
            </div>
          )}

          <FeedbackSection
            projectId={project.id}
            feedback={feedback}
            onFeedbackAdded={handleFeedbackAdded}
          />
        </div>

        <div className="sidebar">
          <div className="interest-card">
            <h3>{t('preReleaseDetail.communityInterest')}
              {/* Project Actions */}
              {user && project.publisher_id === user.id && project.status !== 'cancelled' && (
                <div className="project-actions">
                  {project.status === 'development' && (
                    <button
                      className="release-project-btn"
                      onClick={handleReleaseProject}
                      title="Mark as Released and Notify Users"
                    >
                      <i className="bi bi-rocket"></i>
                      {t('preReleaseDetail.releaseProject')}
                    </button>
                  )}
                  {project.status === 'released' && (
                    <>
                      <button
                        className="notify-users-btn"
                        onClick={() => setShowReleaseNotificationModal(true)}
                        title="Contact Interested Users"
                      >
                        <i className="bi bi-envelope-heart"></i>
                        {t('preReleaseDetail.notifyUsers')}
                      </button>

                    </>
                  )}
                </div>
              )}
            </h3>

            {interestSummary && (
              <div className="interest-stats">
                <div className="stat">
                  <span className="stat-number">{interestSummary.total_interested || 0}</span>
                  <span className="stat-label">{t('preReleaseDetail.peopleInterested')}</span>
                </div>
                <div className="stat">
                  <span className="stat-number">{interestSummary.willing_to_pay || 0}</span>
                  <span className="stat-label">{t('preReleaseDetail.willPurchase')}</span>
                </div>
                <div className="stat committed">
                  <span className="stat-number">{interestSummary.committed_to_buy || 0}</span>
                  <span className="stat-label">{t('preReleaseDetail.committedBuyers')}</span>
                </div>
                {interestSummary.early_bird_committed > 0 && (
                  <div className="stat early-bird">
                    <span className="stat-number">{interestSummary.early_bird_committed || 0}</span>
                    <span className="stat-label">{t('preReleaseDetail.earlyBirdCommitted')}</span>
                  </div>
                )}
                {interestSummary.average_interest_level && (
                  <div className="stat">
                    <span className="stat-number">{interestSummary.average_interest_level}/5</span>
                    <span className="stat-label">{t('preReleaseDetail.avgInterest')}</span>
                  </div>
                )}
                {interestSummary.commitment_rate > 0 && (
                  <div className="stat-percentage">
                    <span className="stat-number">{interestSummary.commitment_rate}%</span>
                    <span className="stat-label">{t('preReleaseDetail.commitmentRate')}</span>
                  </div>
                )}
              </div>
            )}

            {user ? (
              <div className="user-interest-section">
                {project.publisher_id === user.id ? (
                  <div className="publisher-notice">
                    <p>{t('preReleaseDetail.publisherNoticeTitle')}</p>
                    <p>{t('preReleaseDetail.publisherNoticeMessage')}</p>
                  </div>
                ) : project.status === 'released' ? (
                  <div className="purchase-section">
                    {userInterest && userInterest.committed_to_buy && (
                      <div className="prior-commitment">
                        <div className="commitment-badge">{t('preReleaseDetail.priorCommitment')}</div>
                        {userInterest.early_bird_commitment && (
                          <div className="early-bird-note">{t('preReleaseDetail.earlyBirdNote')}</div>
                        )}
                      </div>
                    )}
                    <div className="purchase-actions">
                      <button
                        className="purchase-btn primary"
                        onClick={() => {
                          if (project.demo_url) {
                            window.open(project.demo_url, '_blank');
                          } else {
                            showToast(t('preReleaseDetail.purchaseLinkNotAvailable'), 'warning');
                          }
                        }}
                      >
                        <i className="bi bi-cart"></i>
                        {t('preReleaseDetail.purchaseNow')}
                      </button>
                      {project.demo_url && (
                        <a
                          href={project.demo_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="visit-project-btn secondary"
                        >
                          <i className="bi bi-box-arrow-up-right"></i>
                          Visit Project
                        </a>
                      )}
                    </div>
                  </div>
                ) : userInterest ? (
                  <div className="current-interest">
                    <div className="interest-level">
                      Your Interest: {userInterest.interest_level}/5
                    </div>
                    {userInterest.willing_to_pay && (
                      <div className="willing-to-pay">✓ You're willing to purchase</div>
                    )}
                    {userInterest.committed_to_buy && (
                      <div className="committed-to-buy">✓ You're committed to buy</div>
                    )}
                    {userInterest.early_bird_commitment && (
                      <div className="early-bird-commitment">✓ Early bird commitment</div>
                    )}
                    <button
                      className="update-interest-btn"
                      onClick={() => setShowInterestModal(true)}
                    >
                      Update Interest
                    </button>
                  </div>
                ) : (
                  <button
                    className="express-interest-btn"
                    onClick={() => setShowInterestModal(true)}
                  >
                    Express Interest
                  </button>
                )}
              </div>
            ) : (
              <div className="login-prompt">
                {project.status === 'released' ? (
                  <Link to="/login" className="login-link">
                    Login to purchase
                  </Link>
                ) : (
                  <Link to="/login" className="login-link">
                    Login to express interest
                  </Link>
                )}
              </div>
            )}
          </div>

          <div className="pricing-card">
            <h3>{t('preReleaseDetail.pricingInformation')}</h3>

            <div className="price-details">
              {(() => {
                const pricingText = getDetailedPricingText();
                if (!pricingText) return null;

                return (
                  <>
                    <div className="price-row">
                      <span className="label">{t('preReleaseDetail.expectedPrice')}</span>
                      <span className="price">{pricingText.originalText.replace('Original: ', '')}</span>
                    </div>

                    {pricingText.earlyBirdText && (
                      <div className="price-row discount">
                        <span className="label">{t('preReleaseDetail.earlyBirdPrice')}</span>
                        <span className="discounted-price">
                          {pricingText.earlyBirdText.replace('Early Bird: ', '')}
                        </span>
                      </div>
                    )}

                    {pricingText.savingsText && (
                      <div className="savings-info">
                        {pricingText.savingsText}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {interestSummary?.average_max_price && (
              <div className="community-pricing">
                <div className="price-row">
                  <span className="label">Community Avg:</span>
                  <span className="price">{PricingService.formatPrice(interestSummary.average_max_price)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="timeline-card">
            <h3>{t('preReleaseDetail.developmentTimeline')}</h3>
            <div className="timeline-info">
              {project.estimated_timeline ? (
                <div className="timeline-estimate">
                  <span className="timeline-text">{project.estimated_timeline}</span>
                </div>
              ) : (
                <div className="timeline-tbd">Timeline to be determined</div>
              )}

              <div className="project-status">
                <span className="status-label">{t('preReleaseDetail.statusLabel')}</span>
                <span className={`status-badge ${project.status}`}>
                  {t(`preReleaseDetail.status${project.status.charAt(0).toUpperCase() + project.status.slice(1)}`)}
                </span>
              </div>
            </div>
          </div>

          <div className="disclaimer-card">
            <h4>{t('preReleaseDetail.importantNote')}</h4>
            <p>
              {t('preReleaseDetail.disclaimerText')}
            </p>
          </div>
        </div>
      </div>

      {showInterestModal && (
        <InterestModal
          project={project}
          existingInterest={userInterest}
          onClose={() => setShowInterestModal(false)}
          onInterestSubmitted={handleInterestSubmitted}
        />
      )}

      {showReleaseNotificationModal && (
        <ReleaseNotificationModal
          project={project}
          isOpen={showReleaseNotificationModal}
          onClose={() => setShowReleaseNotificationModal(false)}
          onEmailTemplateGenerate={handleEmailTemplateGenerate}
          onWebsiteMessageSend={handleWebsiteMessageSend}
        />
      )}

    </div>
  );
};